/** Serializes header saves and carries the returned server version into the next edit. */
export function createPurchaseAutosave({ save, onState, onSaved, delay = 650 }) {
  let version = null;
  let draft = null;
  let revision = 0;
  let savedRevision = 0;
  let timer = null;
  let inFlight = false;
  let blocked = false;
  let blockReason = null;
  let disposed = false;

  const schedule = (ms = delay) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; flush(); }, ms);
  };

  const flush = async () => {
    if (disposed) return false;
    if (blocked) return false;
    if (inFlight || !draft || revision === savedRevision) return true;
    const sentRevision = revision;
    const sentDraft = { ...draft };
    inFlight = true;
    onState({ saving: true, dirty: true, error: null });
    try {
      const result = await save({ ...sentDraft, version });
      const nextVersion = result?.data?.data?.version;
      if (result?.data?.success !== true || nextVersion === null || nextVersion === undefined) {
        throw new Error("purchase_save_contract");
      }
      if (disposed) return false;
      version = nextVersion;
      savedRevision = sentRevision;
      onSaved?.(result.data.data);
      onState({ saving: false, dirty: revision !== savedRevision, error: null, savedAt: new Date().toISOString() });
      if (revision !== savedRevision) schedule(0);
      return true;
    } catch (error) {
      if (disposed) return false;
      blocked = true;
      blockReason = error?.response?.status === 409 ? "conflict" : "unknown";
      onState({ saving: false, dirty: true, error: blockReason });
      return false;
    } finally {
      inFlight = false;
    }
  };

  return {
    hydrate(serverVersion) {
      if (revision !== savedRevision) return false;
      version = serverVersion;
      blocked = false;
      blockReason = null;
      return true;
    },
    noteVersion(serverVersion) {
      if (serverVersion === null || serverVersion === undefined) return;
      version = serverVersion;
    },
    edit(nextDraft) {
      if (disposed) return;
      draft = nextDraft;
      revision += 1;
      onState({ saving: inFlight, dirty: true, error: blockReason });
      if (!blocked) schedule();
    },
    flush,
    get dirty() { return revision !== savedRevision; },
    get blocked() { return blocked; },
    dispose() { disposed = true; if (timer) clearTimeout(timer); },
  };
}

/** Runs header and line saves one at a time so a newer version is not overwritten. */
export function createPurchaseSaveChain() {
  let tail = Promise.resolve();
  return (task) => {
    const run = tail.then(() => task(), () => task());
    tail = run.then(() => undefined, () => undefined);
    return run;
  };
}

/**
 * Per-line draft queue. An in-flight save keeps its payload; a newer edit is
 * sent afterwards and the older response is reported as stale.
 */
export function createPurchaseLineQueue({ save, onLine, delay = 650 }) {
  const lines = new Map();
  let disposed = false;

  const schedule = (id, ms) => {
    const line = lines.get(id);
    if (!line || disposed) return;
    if (line.timer) clearTimeout(line.timer);
    line.timer = setTimeout(() => {
      line.timer = null;
      flushLine(id);
    }, ms);
  };

  const flushLine = async (id) => {
    const line = lines.get(id);
    if (disposed || !line || line.inFlight || line.revision === line.savedRevision) return;
    const sentRevision = line.revision;
    const sentDraft = line.draft;
    line.inFlight = true;
    try {
      const result = await save(id, sentDraft);
      if (disposed) return;
      const current = lines.get(id);
      if (!current) return;
      current.inFlight = false;
      if (result?.skipped) {
        if (current.revision !== sentRevision) schedule(id, 0);
        onLine?.(id, { status: "skipped", sentDraft });
        return;
      }
      if (current.revision === sentRevision) {
        current.savedRevision = sentRevision;
        onLine?.(id, { status: "saved", sentDraft, result });
      } else {
        onLine?.(id, { status: "stale", sentDraft, result });
        schedule(id, 0);
      }
    } catch (error) {
      const current = lines.get(id);
      if (current) current.inFlight = false;
      if (!disposed) {
        onLine?.(id, { status: "error", error, sentDraft });
        if (current && current.revision !== sentRevision) schedule(id, 0);
      }
    }
  };

  return {
    edit(id, draft) {
      if (disposed) return;
      const key = String(id);
      const line = lines.get(key) || { revision: 0, savedRevision: 0, draft: null, timer: null, inFlight: false };
      line.draft = draft;
      line.revision += 1;
      lines.set(key, line);
      if (!line.inFlight) schedule(key, delay);
    },
    flush(id) {
      const key = String(id);
      const line = lines.get(key);
      if (line?.timer) {
        clearTimeout(line.timer);
        line.timer = null;
      }
      return flushLine(key);
    },
    async flushAll() {
      const keys = [...lines.keys()];
      await Promise.all(keys.map((key) => {
        const line = lines.get(key);
        if (line?.timer) {
          clearTimeout(line.timer);
          line.timer = null;
        }
        return flushLine(key);
      }));
      return !this.dirty();
    },
    forget(id) {
      const key = String(id);
      const line = lines.get(key);
      if (line?.timer) clearTimeout(line.timer);
      lines.delete(key);
    },
    dirty() {
      for (const line of lines.values()) if (line.revision !== line.savedRevision) return true;
      return false;
    },
    drafts() {
      const result = {};
      for (const [id, line] of lines) {
        if (line.revision !== line.savedRevision && line.draft) result[id] = line.draft;
      }
      return result;
    },
    dispose() {
      disposed = true;
      for (const line of lines.values()) if (line.timer) clearTimeout(line.timer);
    },
  };
}
